import {
  ChangeDetectionStrategy,
  Component,
  type OnDestroy,
} from '@angular/core';
import { Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CurrentBrandScope,
  OrganisationService,
  SoundNotification,
  StafferSettingsStoreService,
} from '@principle-theorem/ng-principle-shared';
import {
  type IBreadcrumb,
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  getNotificationSound,
  NOTIFICATION_SOUNDS,
} from '@principle-theorem/notifications';
import {
  CHART_VIEWS,
  ChartView,
  IStafferSettings,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  Firestore,
  isChanged$,
  snapshot,
} from '@principle-theorem/shared';
import { merge, type Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'pr-account-general',
  templateUrl: './account-general.component.html',
  styleUrls: ['./account-general.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountGeneralComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();

  form = new TypedFormGroup<
    Pick<IStafferSettings, 'charting' | 'chats' | 'notifications'>
  >({
    charting: new TypedFormGroup({
      view: new TypedFormControl(ChartView.Adult, Validators.required),
    }),
    chats: new TypedFormGroup({
      showAllPractices: new TypedFormControl<boolean>(
        false,
        Validators.required
      ),
      notificationSoundOverride: new TypedFormControl<string>(undefined),
    }),
    notifications: new TypedFormGroup({
      patientArrivedSoundOverride: new TypedFormControl<string>(undefined),
    }),
  });
  breadcrumbs$: Observable<IBreadcrumb[]>;
  trackByChartView = TrackByFunctions.variable<ChartView>();
  chartViews = CHART_VIEWS;
  sounds = NOTIFICATION_SOUNDS;

  constructor(
    private _snackBar: MatSnackBar,
    private _currentBrand: CurrentBrandScope,
    private _organisation: OrganisationService,
    private _stafferSettings: StafferSettingsStoreService
  ) {
    this.breadcrumbs$ = this._currentBrand.doc$.pipe(
      filterUndefined(),
      map((brand) => {
        return [
          { label: 'Settings', path: '../../../../' },
          { label: brand.name },
          { label: 'My Account', path: '' },
          { label: 'General' },
        ];
      })
    );

    const chats = this.form.controls.chats as TypedFormGroup<
      Pick<
        IStafferSettings['chats'],
        'showAllPractices' | 'notificationSoundOverride'
      >
    >;

    const notifications = this.form.controls.notifications as TypedFormGroup<
      Pick<IStafferSettings['notifications'], 'patientArrivedSoundOverride'>
    >;

    this._organisation.stafferSettings$
      .pipe(filterUndefined(), take(1), takeUntil(this._onDestroy$))
      .subscribe((settings) =>
        this.form.patchValue(settings, { emitEvent: false })
      );

    merge(
      chats.controls.notificationSoundOverride.valueChanges.pipe(isChanged$()),
      notifications.controls.patientArrivedSoundOverride.valueChanges.pipe(
        isChanged$()
      )
    )
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((notificationSound) => {
        if (!notificationSound) {
          return;
        }

        const selectedSound = getNotificationSound(notificationSound);
        if (!selectedSound) {
          return;
        }

        SoundNotification.playSound(selectedSound);
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async save(): Promise<void> {
    const staffer = await snapshot(
      this._organisation.staffer$.pipe(filterUndefined())
    );
    const settings = await snapshot(this._organisation.stafferSettings$);

    const form = this.form.getRawValue();

    try {
      await Firestore.patchDoc(staffer.ref, {
        settings: {
          ...settings,
          charting: {
            ...settings?.charting,
            view: form.charting.view,
          },
          chats: {
            ...settings?.chats,
            showAllPractices: form.chats.showAllPractices,
            notificationSoundOverride: form.chats.notificationSoundOverride,
          },
          notifications: {
            ...settings?.notifications,
            patientArrivedSoundOverride:
              form.notifications.patientArrivedSoundOverride,
          },
        },
      });
      this._stafferSettings.updateStafferSettings({
        charting: { view: form.charting.view },
        chats: {
          showAllPractices: form.chats.showAllPractices,
          notificationSoundOverride: form.chats.notificationSoundOverride,
        },
        notifications: {
          patientArrivedSoundOverride:
            form.notifications.patientArrivedSoundOverride,
        },
      });
      this._snackBar.open('Changes Saved');
    } catch {
      this._snackBar.open('Error saving changes');
    }
  }
}
